import ReactDOMServer from "react-dom/server";
import styled from "styled-components";

import { colorsPalettes, rgba } from "@similarweb/styles";

import { DataLabel } from "app/pages/industry-analysis/players/table/bubble-chart/chart/DataLabel";
import {
    CHART_HEIGHT,
    CHART_OPTIONS_META_DATA,
    DEFAULT_COLOR,
    QUADRANT_BACKGROUND_IMAGE_PATH,
} from "app/pages/industry-analysis/players/table/bubble-chart/const";
import { getMetaData } from "app/pages/industry-analysis/players/table/bubble-chart/getMetaData";
import type { ETypes, IAxisMetaData, IChartData } from "app/pages/industry-analysis/players/table/bubble-chart/types";
import { AssetsService } from "app/services/AssetsService";
import type { IDurationObject } from "app/services/DurationService";
import { chartTypes } from "app/UtilitiesAndConstants/Constants/ChartTypes";
import combineConfigs from "pro-features/components/Chart/src/combineConfigs";
import noLegendConfig from "pro-features/components/Chart/src/configs/legend/noLegendConfig";
import tooltipContainerConfig from "pro-features/components/Chart/src/configs/tooltip/tooltipContainerConfig";
import noStickyTracking from "pro-features/components/Chart/src/configs/tracking/noStickyTracking";

import { BubbleChartTooltip } from "./tooltip/BubblechartTooltip";

const { blue, carbon } = colorsPalettes;

/**
 * Adds extra space
 * */
const X_AXIS_PADDING = 0.2;
const Y_AXIS_PADDING = 0.5;
const PLOT_LINE_COLOR = blue[100];

const TooltipContainer = styled.div`
    padding: 8px 12px 12px 12px;
    background-color: ${carbon[0]};
    box-shadow: 0 3px 6px 0 ${rgba(carbon[200], 0.5)};
    border-radius: 5px;
`;
let displayAverageTooltip = false;

const getPlotLines = (showAverage: boolean, axis: IAxisMetaData) => {
    return [
        {
            color: PLOT_LINE_COLOR,
            width: 3,
            value: showAverage ? axis.average : null,
            events: {
                mouseover: function (e) {
                    if (!this.options) {
                        return;
                    }
                    const series = this.axis.series[0],
                        chart = series.chart,
                        PointClass = series.pointClass,
                        tooltip = chart.tooltip,
                        point = new PointClass().init(series, ["Plotline", this.options.value]),
                        normalizedEvent = chart.pointer.normalize(e);

                    point.tooltipPos = [
                        normalizedEvent.chartX - chart.plotLeft,
                        normalizedEvent.chartY - chart.plotTop,
                    ];
                    displayAverageTooltip = true;
                    tooltip.refresh(point);
                },
                mouseout: function (e) {
                    this.axis.chart.tooltip.hide();
                    displayAverageTooltip = false;
                },
            },
        },
    ];
};

const getAxis = (axis: IAxisMetaData, axisPadding: number, showAverage, formatter) => {
    const min =
        axis.range.to > 0
            ? axis.range.from - (axis.range.to * (1 + axisPadding) - axis.range.to)
            : axis.range.from * (1 + axisPadding);
    const max =
        axis.range.to > 0
            ? axis.range.to * (1 + axisPadding)
            : axis.range.to + (axis.range.from - axis.range.from * (1 + axisPadding));
    return {
        lineColor: "transparent",
        gridLineColor: "transparent",
        min,
        max,
        startOnTick: false,
        plotLines: getPlotLines(showAverage, axis),
        endOnTick: false,
        labels: {
            formatter: function () {
                return formatter(this.value);
            },
        },
    };
};

const ARROW_SIZE = 5;
const connectionLines = [];
const arrowHeads = [];

export const getChartConfig = (
    chartData: IChartData,
    type: ETypes,
    showAverage: boolean,
    isPeriodOverPeriod: boolean,
    filters: IDurationObject<string>,
) => {
    const metaData = getMetaData(chartData);
    const chartOptionsMetaData = CHART_OPTIONS_META_DATA[type];
    const { xAxis, yAxis } = metaData;
    const xAxisFormatter = ({ value }) => chartOptionsMetaData.xAxis.formatter(value);
    const yAxisFormatter = ({ value }) => chartOptionsMetaData.yAxis.formatter(value);
    const xAxisConfig = getAxis(xAxis, X_AXIS_PADDING, showAverage, chartOptionsMetaData.xAxis.formatter);
    const yAxisConfig = getAxis(yAxis, Y_AXIS_PADDING, showAverage, chartOptionsMetaData.yAxis.formatter);

    const chartConfig = combineConfigs({ xAxisFormatter, yAxisFormatter }, [
        noStickyTracking,
        noLegendConfig,
        {
            chart: {
                type: chartTypes.BUBBLE,
                height: CHART_HEIGHT.length,
                plotBackgroundImage: AssetsService.assetUrl(QUADRANT_BACKGROUND_IMAGE_PATH),
                gridLineColor: "transparent",
                events: {
                    render: function () {
                        if (connectionLines.length) {
                            connectionLines.forEach((line) => line.element.remove());
                        }
                        if (arrowHeads.length) {
                            arrowHeads.forEach((arrowHead) => arrowHead.element.remove());
                        }

                        this.series.forEach((item) => {
                            const currentPoint = item.data[0];
                            const associatedPoint = item.data[1];
                            const { color } = item.data[0];

                            if (associatedPoint) {
                                const center1 = {
                                    x: currentPoint.plotX + item.group.translateX,
                                    y: currentPoint.plotY + item.group.translateY,
                                };

                                const center2 = {
                                    x: associatedPoint.plotX + item.group.translateX,
                                    y: associatedPoint.plotY + item.group.translateY,
                                };

                                const [radius1, radius2] = item.data.map((point) => point.displayZ / 2);

                                const distance = Math.hypot(center2.x - center1.x, center2.y - center1.y);
                                const sumOfRadii = radius1 + radius2;

                                if (distance < sumOfRadii) {
                                    return;
                                }

                                const currentPeriodCircleEdgePoint = {
                                    x:
                                        center1.x +
                                        (radius1 * (center2.x - center1.x)) /
                                            Math.hypot(center2.x - center1.x, center2.y - center1.y),
                                    y:
                                        center1.y +
                                        (radius1 * (center2.y - center1.y)) /
                                            Math.hypot(center2.x - center1.x, center2.y - center1.y),
                                };
                                const comparedPeriodCircleEdgePoint = {
                                    x:
                                        center2.x -
                                        (radius2 * (center2.x - center1.x)) /
                                            Math.hypot(center2.x - center1.x, center2.y - center1.y),
                                    y:
                                        center2.y -
                                        (radius2 * (center2.y - center1.y)) /
                                            Math.hypot(center2.x - center1.x, center2.y - center1.y),
                                };

                                const connectionLine = this.renderer
                                    .path([
                                        ["M", currentPeriodCircleEdgePoint.x, currentPeriodCircleEdgePoint.y],
                                        ["L", comparedPeriodCircleEdgePoint.x, comparedPeriodCircleEdgePoint.y],
                                    ])
                                    .attr({
                                        stroke: color ?? DEFAULT_COLOR,
                                        "stroke-width": 1,
                                    })
                                    .add();
                                connectionLine.name = item.name;

                                const angle = Math.atan2(
                                    comparedPeriodCircleEdgePoint.y - currentPeriodCircleEdgePoint.y,
                                    comparedPeriodCircleEdgePoint.x - currentPeriodCircleEdgePoint.x,
                                );
                                const arrowPoint1 = {
                                    x: currentPeriodCircleEdgePoint.x + ARROW_SIZE * Math.cos(angle - Math.PI / 6),
                                    y: currentPeriodCircleEdgePoint.y + ARROW_SIZE * Math.sin(angle - Math.PI / 6),
                                };
                                const arrowPoint2 = {
                                    x: currentPeriodCircleEdgePoint.x + ARROW_SIZE * Math.cos(angle + Math.PI / 6),
                                    y: currentPeriodCircleEdgePoint.y + ARROW_SIZE * Math.sin(angle + Math.PI / 6),
                                };

                                const arrowHead = this.renderer
                                    .path([
                                        "M",
                                        currentPeriodCircleEdgePoint.x,
                                        currentPeriodCircleEdgePoint.y,
                                        "L",
                                        arrowPoint1.x,
                                        arrowPoint1.y,
                                        "M",
                                        currentPeriodCircleEdgePoint.x,
                                        currentPeriodCircleEdgePoint.y,
                                        "L",
                                        arrowPoint2.x,
                                        arrowPoint2.y,
                                    ])
                                    .attr({
                                        stroke: color,
                                        zIndex: 5,
                                    })
                                    .add();
                                arrowHead.name = item.name;

                                connectionLines.push(connectionLine);
                                arrowHeads.push(arrowHead);
                            }
                        });
                    },
                },
            },
            tooltip: {
                ...tooltipContainerConfig().tooltip,
                padding: 0,
                useHTML: true,
                outside: true,
                split: true,
                formatter: function () {
                    const point = this.point;
                    if (!point) {
                        return null;
                    }
                    const { options, series } = point;
                    const { isPreviousPeriod } = options;
                    const { userOptions } = series;
                    const { name, subtitle, favicon } = userOptions;

                    return ReactDOMServer.renderToString(
                        <BubbleChartTooltip
                            isAverage={displayAverageTooltip}
                            isPeriodOverPeriod={isPeriodOverPeriod}
                            isPreviousPeriod={isPreviousPeriod}
                            series={series}
                            options={options}
                            subtitle={subtitle}
                            favicon={favicon}
                            filters={filters}
                            metaData={metaData}
                            type={type}
                            entityDisplayName={name}
                        />,
                    );
                },
            },
            xAxis: xAxisConfig,
            yAxis: yAxisConfig,
            plotOptions: {
                series: {
                    states: {
                        inactive: {
                            enabled: true,
                            opacity: 0.4,
                        },
                        hover: {
                            enabled: false,
                        },
                    },
                },
                bubble: {
                    marker: {
                        enabled: false,
                    },
                    dataLabels: {
                        enabled: true,
                        useHTML: true,
                        allowOverlap: true,
                        animation: false,
                        formatter: function () {
                            const { displayZ, isPreviousPeriod } = this.point.options;
                            const { color } = this.point || DEFAULT_COLOR;
                            const { favicon, subtitle } = this.point.series.userOptions;

                            return ReactDOMServer.renderToString(
                                <DataLabel
                                    diameter={{ length: displayZ, unit: "px" }}
                                    color={color}
                                    favicon={favicon}
                                    isSegment={!!subtitle}
                                    isPreviousPeriod={isPreviousPeriod}
                                />,
                            );
                        },
                    },
                    point: {
                        events: {
                            mouseOver: function () {
                                connectionLines.forEach(
                                    (line) => line.name !== this.series.name && line.attr({ opacity: 0.4 }),
                                );
                                arrowHeads.forEach(
                                    (arrowHead) =>
                                        arrowHead.name !== this.series.name && arrowHead.attr({ opacity: 0.4 }),
                                );
                            },
                            mouseOut: function () {
                                connectionLines.forEach((line) => line.attr({ opacity: 1 }));
                                arrowHeads.forEach((arrowHead) => arrowHead.attr({ opacity: 1 }));
                            },
                        },
                    },
                },
            },
            series: chartData,
        },
    ]);
    return chartConfig;
};
